import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "components/CustomButtons/Button.js";
import SnackBarWiiba from "components/Snackbar/SnackBarWiiba";
import useMessage from "../../hooks/useMessage";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  header: {
    background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
    padding: "16px 24px", color: "#fff",
  },
  headerTitle: { fontSize: 16, fontWeight: 700, margin: 0, color: "#fff" },
  headerSub: { fontSize: 12, color: "#cce5ff", margin: 0 },
  section: {
    backgroundColor: "#f8f9fa", borderRadius: 8, padding: "12px 16px",
    marginBottom: 12, border: "1px solid #e0e0e0",
  },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, color: "#546e7a",
    textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8,
  },
  row: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "4px 0",
  },
  rowIndent: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "3px 0 3px 16px",
  },
  label: { fontSize: 13, color: "#546e7a" },
  labelSmall: { fontSize: 12, color: "#90a4ae" },
  value: { fontSize: 14, fontWeight: 600, color: "#263238" },
  valueGreen: { fontSize: 14, fontWeight: 700, color: "#2e7d32" },
  valueRed: { fontSize: 14, fontWeight: 700, color: "#c62828" },
  valueBlue: { fontSize: 14, fontWeight: 600, color: "#1565c0" },
  totalRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "8px 0", borderTop: "2px solid #e0e0e0", marginTop: 4,
  },
  totalLabel: { fontSize: 14, fontWeight: 700, color: "#263238" },
  totalValue: { fontSize: 16, fontWeight: 700, color: "#1565c0" },
  highlight: {
    borderRadius: 8, padding: "12px 16px", marginBottom: 12,
    display: "flex", justifyContent: "space-between", alignItems: "center",
  },
}));

const fmtMoney = val =>
  "$" + parseFloat(val || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 });

const fmtDate = str => {
  if (!str) return "";
  const d = new Date(str);
  return isNaN(d) ? str : d.toLocaleString("es-MX", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
};

const ModalPrevioCierre = ({
  modalCierreCaja, setModalCierreCaja,
  cierreCaja, getGeneraCierreCaja, usuario
}) => {
  const classes = useStyles();
  const { opensnack, typesnackbar, message, handleMuestraSnack, handleClosesSnack } = useMessage();
  const [montoDeclarado, setMontoDeclarado] = useState("");
  const [confirmando, setConfirmando] = useState(false);

  const data = cierreCaja;
  const esperadoEfectivo = parseFloat(data?.ESPERADO_EFECTIVO || 0);
  const declarado = parseFloat(montoDeclarado) || 0;
  const diferencia = montoDeclarado !== "" ? declarado - esperadoEfectivo : null;

  const handleConfirmar = async () => {
    if (montoDeclarado === "") {
      handleMuestraSnack("Ingrese el efectivo que tiene en caja", "warning");
      return;
    }
    setConfirmando(true);
    await getGeneraCierreCaja(usuario.userId, 0, true, parseFloat(montoDeclarado));
    setConfirmando(false);
    setModalCierreCaja(false);
    setMontoDeclarado("");
  };

  const handleClose = () => {
    setModalCierreCaja(false);
    setMontoDeclarado("");
  };

  return (
    <>
      <SnackBarWiiba opensnack={opensnack} tipoSnackBar={typesnackbar}
        mensaje={message} handleClosesSnack={handleClosesSnack} />

      <Dialog open={modalCierreCaja} onClose={handleClose} maxWidth="sm" fullWidth>
        <div className={classes.header}>
          <p className={classes.headerTitle}>Cierre de Caja</p>
          {data && <p className={classes.headerSub}>Apertura: {fmtDate(data.FECHA_CIERRE_INI)}</p>}
        </div>

        <DialogContent style={{ padding: "20px 24px" }}>
          {!data ? (
            <div style={{ textAlign: "center", padding: 32 }}>
              <CircularProgress />
              <Typography style={{ marginTop: 12, color: "#666" }}>Calculando resumen...</Typography>
            </div>
          ) : (
            <>
              {/* Monto inicial */}
              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>Apertura</Typography>
                <div className={classes.row}>
                  <Typography className={classes.label}>Monto inicial</Typography>
                  <Typography className={classes.value}>{fmtMoney(data.MONTO_INICIAL)}</Typography>
                </div>
              </div>

              {/* Entradas desglosadas */}
              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>Entradas</Typography>
                <div className={classes.rowIndent}>
                  <Typography className={classes.labelSmall}>Efectivo</Typography>
                  <Typography className={classes.valueGreen}>{fmtMoney(data.ENTRADAS_EFECTIVO)}</Typography>
                </div>
                <div className={classes.rowIndent}>
                  <Typography className={classes.labelSmall}>Transferencia</Typography>
                  <Typography className={classes.valueBlue}>{fmtMoney(data.ENTRADAS_TRANSFERENCIA)}</Typography>
                </div>
                <div className={classes.totalRow}>
                  <Typography className={classes.totalLabel}>Total entradas</Typography>
                  <Typography className={classes.valueGreen}>{fmtMoney(data.TOTAL_ENTRADAS)}</Typography>
                </div>
              </div>

              {/* Salidas */}
              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>Salidas</Typography>
                <div className={classes.row}>
                  <Typography className={classes.label}>Egresos y movimientos</Typography>
                  <Typography className={classes.valueRed}>{fmtMoney(data.TOTAL_SALIDAS)}</Typography>
                </div>
              </div>

              {/* Esperado en efectivo */}
              <div className={classes.highlight}
                style={{ backgroundColor: "#e8f5e9", border: "1px solid #a5d6a7" }}>
                <div>
                  <Typography style={{ fontSize: 12, color: "#2e7d32", fontWeight: 600 }}>
                    Esperado en efectivo
                  </Typography>
                  <Typography variant="caption" style={{ color: "#66bb6a" }}>
                    Inicial + entradas efectivo − salidas
                  </Typography>
                </div>
                <Typography style={{ fontSize: 22, fontWeight: 700, color: "#2e7d32" }}>
                  {fmtMoney(esperadoEfectivo)}
                </Typography>
              </div>

              {/* Esperado en transferencia (informativo) */}
              <div className={classes.highlight}
                style={{ backgroundColor: "#e3f2fd", border: "1px solid #90caf9" }}>
                <div>
                  <Typography style={{ fontSize: 12, color: "#1565c0", fontWeight: 600 }}>
                    Cobrado por transferencia
                  </Typography>
                  <Typography variant="caption" style={{ color: "#64b5f6" }}>
                    Solo informativo — no requiere conteo
                  </Typography>
                </div>
                <Typography style={{ fontSize: 18, fontWeight: 700, color: "#1565c0" }}>
                  {fmtMoney(data.ENTRADAS_TRANSFERENCIA)}
                </Typography>
              </div>

              <Divider style={{ margin: "8px 0 16px" }} />

              {/* Conteo de efectivo */}
              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>Conteo de efectivo al cierre</Typography>
                <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: 12 }}>
                  Cuente el dinero físico en caja e ingrese el total.
                </Typography>
                <TextField
                  label="Efectivo en caja"
                  type="number"
                  value={montoDeclarado}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === "" || parseFloat(val) >= 0) setMontoDeclarado(val);
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    style: { fontSize: 18, fontWeight: 700 },
                    inputProps: { min: 0 }
                  }}
                />
              </div>

              {/* Diferencia */}
              {diferencia !== null && (
                <div className={classes.highlight}
                  style={{
                    backgroundColor: diferencia === 0 ? "#e8f5e9" : diferencia > 0 ? "#fff8e1" : "#ffebee",
                    border: `1px solid ${diferencia === 0 ? "#a5d6a7" : diferencia > 0 ? "#ffe082" : "#ef9a9a"}`
                  }}>
                  <Typography style={{ fontWeight: 700, fontSize: 14 }}>
                    {diferencia === 0 ? "✓ Caja cuadrada" : diferencia > 0 ? "⚠ Sobrante en efectivo" : "⚠ Faltante en efectivo"}
                  </Typography>
                  <Typography style={{
                    fontWeight: 700, fontSize: 18,
                    color: diferencia === 0 ? "#2e7d32" : diferencia > 0 ? "#f57f17" : "#c62828"
                  }}>
                    {diferencia > 0 ? "+" : ""}{fmtMoney(diferencia)}
                  </Typography>
                </div>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={handleClose} color="danger" size="sm">Cancelar</Button>
          <Button onClick={handleConfirmar} color="success" size="sm"
            disabled={!data || confirmando}>
            {confirmando ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "Confirmar Cierre"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalPrevioCierre;
